import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import type { Auth } from 'firebase/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { from, Subject, throwError } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { AUTH } from 'src/app/firebase-injection';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { UserGroupService } from 'src/app/services/user-group.service';
import { UserService } from 'src/app/services/user.service';
import { LocaleSwitchService } from 'src/app/services/locale-switch.service';
import { needsEmailVerification } from 'src/app/services/auth.utils';

@Component({
  selector: 'app-accept-invite',
  templateUrl: './accept-invite.component.html',
  styleUrls: ['./accept-invite.component.scss'],
  imports: [MatCard, MatCardContent, MatButton, MatProgressSpinner],
})
export class AcceptInviteComponent implements OnInit, OnDestroy {
  groupId: string | null = null;
  inviteType: 'watcher' | 'contributor' = 'watcher';
  inviteToken: string | null = null;
  groupName: string | null = null;
  loading = true;
  joined = false;
  error: string | null = null;
  infoMessage: string | null = null;
  blocked = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    @Inject(AUTH) private auth: Auth,
    private localStorage: LocalStorageService,
    private userGroupService: UserGroupService,
    private userService: UserService,
    private localeSwitch: LocaleSwitchService
  ) {}

  ngOnInit(): void {
    const url = this.router.url;
    if (url.startsWith('/invite')) {
      this.localStorage.setInviteLink(url);
    }

    from(this.auth.authStateReady())
      .pipe(
        switchMap(() => from(this.maybeRedirectToPreferredLocale())),
        switchMap((didRedirect) => {
          if (didRedirect) {
            return throwError(() => new Error('Redirecting'));
          }
          return this.route.queryParams.pipe(take(1));
        }),
        switchMap((params) => {
          this.groupId = params['group'] || null;
          this.inviteType =
            params['type'] === 'contributor' ? 'contributor' : 'watcher';
          this.inviteToken = params['token'] || null;

          if (!this.auth.currentUser) {
            this.router.navigate(['/auth']);
            return throwError(() => new Error('Not authenticated'));
          }

          if (needsEmailVerification(this.auth.currentUser)) {
            this.localStorage.setInviteLink(this.router.url);
            this.router.navigate(['/verify-email']);
            return throwError(() => new Error('Email not verified'));
          }

          if (!this.groupId || !this.inviteToken) {
            return throwError(() => new Error('Invalid invite link'));
          }

          return this.userGroupService.getUserGroup(this.groupId);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (group) => {
          const expected =
            this.inviteType === 'contributor'
              ? group.inviteTokenContributor
              : group.inviteTokenWatcher;
          if (!expected || expected !== this.inviteToken) {
            this.error = $localize`:@@InvalidInviteToken:This invite link is no longer valid.`;
            this.loading = false;
            return;
          }
          this.groupName = group.name;
          const uid = this.auth.currentUser?.uid;
          if (uid && group.authorId === uid) {
            this.blocked = true;
            this.infoMessage = $localize`:@@AcceptInviteOwnGroup:This is your own group. You cannot join it as a member.`;
            this.localStorage.setInviteLink(undefined);
            this.loading = false;
            return;
          }
          const isWatcher = !!uid && (group.watcherIds ?? []).includes(uid);
          const isContributor =
            !!uid && (group.contributorIds ?? []).includes(uid);
          if (
            (this.inviteType === 'watcher' && isWatcher) ||
            (this.inviteType === 'contributor' && isContributor)
          ) {
            this.joined = true;
            this.infoMessage = $localize`:@@AcceptInviteAlreadyMember:You are already a member of this group.`;
            this.localStorage.setInviteLink(undefined);
            this.loading = false;
            return;
          }
          if (
            (this.inviteType === 'watcher' && isContributor) ||
            (this.inviteType === 'contributor' && isWatcher)
          ) {
            this.blocked = true;
            this.infoMessage = isContributor
              ? $localize`:@@AcceptInviteAlreadyContributor:You are already a contributor of this group. You cannot also join as a watcher.`
              : $localize`:@@AcceptInviteAlreadyWatcher:You are already a watcher of this group. You cannot also join as a contributor.`;
            this.localStorage.setInviteLink(undefined);
            this.loading = false;
            return;
          }
          this.loading = false;
        },
        error: (err) => {
          if (
            err?.message === 'Not authenticated' ||
            err?.message === 'Redirecting' ||
            err?.message === 'Email not verified'
          ) {
            return;
          }
          this.error = $localize`:@@AcceptInviteGroupNotFound:Group not found.`;
          this.loading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  joinTeamButtonClicked() {
    if (!this.groupId || !this.inviteToken) return;

    this.loading = true;

    from(this.auth.authStateReady())
      .pipe(
        switchMap(() => {
          const user = this.auth.currentUser;
          if (!user) {
            this.localStorage.setInviteLink(this.router.url);
            this.router.navigate(['/auth']);
            return throwError(() => new Error('Not authenticated'));
          }
          if (needsEmailVerification(user)) {
            this.localStorage.setInviteLink(this.router.url);
            this.router.navigate(['/verify-email']);
            return throwError(() => new Error('Email not verified'));
          }
          return this.userGroupService.addUserByInviteLink(
            this.groupId!,
            user.uid,
            this.inviteType,
            this.inviteToken!
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.joined = true;
          this.infoMessage = null;
          this.loading = false;
          this.localStorage.setInviteLink(undefined);
        },
        error: (err) => {
          if (
            err?.message === 'Not authenticated' ||
            err?.message === 'Email not verified'
          ) {
            return;
          }
          if (
            err?.code === 'permission-denied' ||
            err?.message === 'Invalid invite token'
          ) {
            this.error = $localize`:@@InvalidInviteToken:This invite link is no longer valid.`;
          } else {
            this.error = $localize`:@@AcceptInviteJoinFailed:Could not join the group. Please try again.`;
          }
          this.loading = false;
        },
      });
  }

  navigateToGroup() {
    this.router.navigate(['/app/user-groups', this.groupId]);
  }

  private async maybeRedirectToPreferredLocale(): Promise<boolean> {
    const preferred = await this.resolvePreferredLocale();
    if (!preferred) return false;
    const active = this.localeSwitch.getActiveLocale();
    if (preferred === active) return false;
    const { pathname, search, hash } = window.location;
    const without = pathname.replace(/^\/(de|en)(?=\/|$)/, '');
    const normalized = without.startsWith('/') ? without : `/${without}`;
    window.location.assign(`/${preferred}${normalized}${search}${hash}`);
    return true;
  }

  private async resolvePreferredLocale(): Promise<'de' | 'en' | null> {
    const user = this.auth.currentUser;
    if (user) {
      const userDoc = await this.userService.getUser(user.uid);
      const stored = userDoc?.preferredLanguage;
      if (stored === 'de' || stored === 'en') return stored;
    }
    const browser = (navigator.language || '').toLowerCase();
    if (browser.startsWith('de')) return 'de';
    if (browser.startsWith('en')) return 'en';
    return null;
  }
}
